/**
 * screenLayout.ts
 * 
 * App.vue から画面レイアウト・印刷・マルチスクリーン関連のロジックを切り出したモジュール。
 */

import maplibregl from 'maplibre-gl'
import { closeAllPopups } from '@/js/popup'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VM = any

/**
 * 印刷モードの開始/終了を切り替える
 */
export function handlePrint(vm: VM, isClose: boolean): void {
  const map00Div = document.getElementById('map00')
  // 印刷モード終了前に2画面状態を保存（v-show制御のためprintMapで判定）
  const wasDualMap = vm.s_printMap === 'map03' || vm.s_printMap === 'map02'
  setTimeout(() => {
    if (isClose === true) {
      // 印刷モード終了時は同期を停止
      vm.stopPrintSync()
      vm.stopMapSync()
      // 詳細/俯瞰モードのオフセットをリセットし、map02をmap01のズームに合わせる
      if (vm.printZoomOffset !== 0) {
        const map01 = vm.$store.state.map01
        const map02 = vm.$store.state.map02
        if (map01 && map02) {
          map02.jumpTo({
            center: map01.getCenter(),
            zoom: map01.getZoom(),
            bearing: map01.getBearing(),
            pitch: map01.getPitch()
          })
        }
        vm.printZoomOffset = 0
      }
      // printMapを初期値に戻し、mapSizeも通常に復帰
      vm.s_printMap = 'map01'
      vm.mapSize.map01.width = '100%'
      vm.mapSize.map01.height = '100%'
      vm.mapSize.map02.width = '50%'
      vm.mapSize.map02.height = '100%'
      vm.mapSize.map02.top = 0
      vm.mapSize.map02.right = 0
      if (wasDualMap) {
        // 印刷2画面→通常2画面に戻す
        // handlePrint本体で既にbtnClickSplit()→1画面化済み
        // なので再度btnClickSplit()で2画面に切り替え
        vm.btnClickSplit()
      }
    }
  }, 100)
  vm.attributionControl = new maplibregl.AttributionControl()
  vm.$store.state.map01.addControl(vm.attributionControl, 'bottom-right')
  const el = document.getElementById('terrain-btn-div-map01')!
  const parent = el.parentNode!
  const next = el.nextSibling               // 元の"次"を覚えておく
  // @ts-ignore
  const grandParent = parent.parentNode.parentNode || document.body
  if (isClose === true) {
    // 印刷モード終了時：PC時は中央表示、スマホ時は非表示
    vm.isRightDiv2 = false
    updateScaleDisplay(vm, false)
    initScaleDraggable(vm)
    if (next) {
      parent.insertBefore(el, next)             // 元の "次Sibling" の前に挿入
    } else {
      parent.appendChild(el)                    // 末尾だったら親の最後に戻す
    }
  } else {
    // 印刷モード開始時：スケールを表示
    vm.isRightDiv2 = true
    updateScaleDisplay(vm, true)

    parent.removeChild(el)
    grandParent.appendChild(el)
  }

  // 2. attributionコントロールのDOM取得
  const attribDom = document.querySelector('.maplibregl-ctrl-attrib')

  if (attribDom) {
    // 3. 好きな右下用のコンテナ（例えばbody直下や専用div）に移動
    const customContainer = document.getElementById('my-attrib-container')
    if (customContainer) {
      customContainer.appendChild(attribDom)
    } else {
      // 無ければ自作してbodyに追加
      const div = document.createElement('div')
      div.id = 'my-attrib-container'
      // CSSで右下に配置
      div.style.position = 'fixed'
      div.style.right = '16px'
      div.style.bottom = '16px'
      div.style.zIndex = '1000'
      div.appendChild(attribDom)
      document.body.appendChild(div)
    }

  }

  vm.s_map2Flg = true
  vm.btnClickSplit()

  if (vm.originalStyle) {
    map00Div!.style.width = vm.originalStyle.width
    map00Div!.style.height = vm.originalStyle.height
    // transform scaleをリセット
    map00Div!.style.transform = 'none'
    map00Div!.style.transformOrigin = 'initial'
    vm.s_isPrint = false
    vm.originalStyle = null
    vm.$store.state.map01.removeControl(vm.attributionControl)
    const customContainer = document.getElementById('my-attrib-container')
    if (customContainer) {
      customContainer.remove()
    }
    return
  }
  // 元のサイズを保存
  vm.originalStyle = {
    width: map00Div!.style.width,
    height: map00Div!.style.height
  }
  vm.s_isPrint = true

  function hideAllDialogs(dialogs: Record<string, Record<string, HTMLElement>>) {
    for (const dialogKey in dialogs) {
      // 'drawConfigDialog' と 'drawShareDialog' の場合はスキップ
      if (dialogKey === 'drawConfigDialog' || dialogKey === 'drawShareDialog') continue
      const dialog = dialogs[dialogKey]
      for (const mapKey in dialog) {
        if (
          dialog[mapKey] &&
          dialog[mapKey].style &&
          typeof dialog[mapKey].style === 'object'
        ) {
          dialog[mapKey].style.display = 'none'
        }
      }
    }
  }
  hideAllDialogs(vm.$store.state.dialogs)

  function setAllDisplayNone(obj: Record<string, HTMLElement[]>) {
    for (const mapKey in obj) {
      const arr = obj[mapKey]
      if (Array.isArray(arr)) {
        arr.forEach((item: HTMLElement) => {
          if (item && item.style && typeof item.style === 'object') {
            item.style.display = 'none'
          }
        })
      }
    }
  }
  setAllDisplayNone(vm.$store.state.dialogsInfo)
  setAllDisplayNone(vm.$store.state.dialog2)

  vm.directionChange()
}

/**
 * 画面構成を取得する関数
 * シングル/デュアル画面、縦/横配置を検出
 */
export function getScreenConfiguration(vm: VM) {
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const availWidth = window.screen.availWidth
  const availHeight = window.screen.availHeight

  // デュアルディスプレイの検出
  // 利用可能な画面幅が実際の画面幅より大きい場合、デュアルディスプレイの可能性
  const isDualScreen = availWidth > screenWidth || availHeight > screenHeight

  let orientation = 'single'
  if (isDualScreen) {
    // 横並び: 幅が高さより大幅に大きい
    // 縦並び: 高さが幅より大幅に大きい
    const ratio = availWidth / availHeight
    if (ratio > 1.5) {
      orientation = 'horizontal' // 横並び
    } else if (ratio < 0.67) {
      orientation = 'vertical' // 縦並び
    } else {
      orientation = 'horizontal' // デフォルトは横並び
    }
  }

  const isDualMap = vm.s_map2Flg
  const mapOrientation = isDualMap
    ? (window.innerWidth > 1000 ? 'horizontal' : 'vertical')
    : 'single'

  return {
    isDualScreen,
    orientation, // 'single', 'horizontal', 'vertical'
    availWidth,
    availHeight,
    mapLayout: {
      isDualMap,
      orientation: mapOrientation // 'single', 'horizontal', 'vertical'
    }
  }
}

/**
 * 画面構成が変わったときに位置を調整する関数
 */
export function adjustPositionForScreenChange(
  pos: { leftPct: number; topPct: number; [key: string]: unknown },
  savedConfig: ReturnType<typeof getScreenConfiguration> | null,
  currentConfig: ReturnType<typeof getScreenConfiguration>
) {
  // 保存された設定がない場合はそのまま
  if (!savedConfig) return pos

  const screenConfigChanged =
    savedConfig.isDualScreen !== currentConfig.isDualScreen ||
    savedConfig.orientation !== currentConfig.orientation

  let adjustedLeftPct = pos.leftPct
  let adjustedTopPct = pos.topPct

  if (screenConfigChanged) {
    // ディスプレイ構成の変化を考慮
    if (!savedConfig.isDualScreen && currentConfig.isDualScreen) {
      // シングル → デュアル画面
      if (currentConfig.orientation === 'horizontal') {
        adjustedLeftPct = pos.leftPct * 0.5
        adjustedTopPct = pos.topPct
      } else if (currentConfig.orientation === 'vertical') {
        adjustedLeftPct = pos.leftPct
        adjustedTopPct = pos.topPct * 0.5
      }
    } else if (savedConfig.isDualScreen && !currentConfig.isDualScreen) {
      // デュアル → シングル画面
      if (savedConfig.orientation === 'horizontal') {
        adjustedLeftPct = Math.min(pos.leftPct * 2, 0.8)
        adjustedTopPct = pos.topPct
      } else if (savedConfig.orientation === 'vertical') {
        adjustedLeftPct = pos.leftPct
        adjustedTopPct = Math.min(pos.topPct * 2, 0.8)
      }
    }

    if (savedConfig.isDualScreen && currentConfig.isDualScreen &&
      savedConfig.orientation !== currentConfig.orientation) {
      if (savedConfig.orientation === 'horizontal' && currentConfig.orientation === 'vertical') {
        adjustedLeftPct = Math.min(pos.leftPct * 2, 0.8)
        adjustedTopPct = pos.topPct * 0.5
      } else if (savedConfig.orientation === 'vertical' && currentConfig.orientation === 'horizontal') {
        adjustedLeftPct = pos.leftPct * 0.5
        adjustedTopPct = Math.min(pos.topPct * 2, 0.8)
      }
    }
  }

  const savedMapLayout = savedConfig.mapLayout
  const currentMapLayout = currentConfig.mapLayout
  const mapLayoutChanged = savedMapLayout && currentMapLayout && (
    savedMapLayout.isDualMap !== currentMapLayout.isDualMap ||
    savedMapLayout.orientation !== currentMapLayout.orientation
  )

  if (mapLayoutChanged) {
    const baseLeft = adjustedLeftPct ?? pos.leftPct
    const baseTop = adjustedTopPct ?? pos.topPct

    if (!savedMapLayout.isDualMap && currentMapLayout.isDualMap) {
      // 1画面 → 2画面
      if (currentMapLayout.orientation === 'horizontal') {
        adjustedLeftPct = Math.min(baseLeft * 0.5, 0.8)
        adjustedTopPct = baseTop
      } else if (currentMapLayout.orientation === 'vertical') {
        adjustedLeftPct = baseLeft
        adjustedTopPct = Math.min(baseTop * 0.5, 0.8)
      }
    } else if (savedMapLayout.isDualMap && !currentMapLayout.isDualMap) {
      // 2画面 → 1画面
      if (savedMapLayout.orientation === 'horizontal') {
        adjustedLeftPct = Math.min(baseLeft * 2, 0.8)
        adjustedTopPct = baseTop
      } else if (savedMapLayout.orientation === 'vertical') {
        adjustedLeftPct = baseLeft
        adjustedTopPct = Math.min(baseTop * 2, 0.8)
      }
    } else if (savedMapLayout.isDualMap && currentMapLayout.isDualMap &&
      savedMapLayout.orientation !== currentMapLayout.orientation) {
      // 2画面間での縦横切り替え
      if (savedMapLayout.orientation === 'horizontal' && currentMapLayout.orientation === 'vertical') {
        adjustedLeftPct = Math.min(baseLeft * 2, 0.8)
        adjustedTopPct = Math.min(baseTop * 0.5, 0.8)
      } else if (savedMapLayout.orientation === 'vertical' && currentMapLayout.orientation === 'horizontal') {
        adjustedLeftPct = Math.min(baseLeft * 0.5, 0.8)
        adjustedTopPct = Math.min(baseTop * 2, 0.8)
      }
    }
  }

  return {
    ...pos,
    leftPct: adjustedLeftPct,
    topPct: adjustedTopPct
  }
}

/**
 * スケール表示を制御する関数
 * isPrintMode: true = 印刷モード, false = 通常モード
 */
export function updateScaleDisplay(vm: VM, isPrintMode = false): void {
  const isSmartPhone = vm.s_isSmartPhone
  const currentMapLayout = isPrintMode ? null : getScreenConfiguration(vm).mapLayout
  const hasLayoutHistory = !isPrintMode && vm.lastScaleMapLayout !== null
  const mapLayoutChanged = !isPrintMode && hasLayoutHistory && currentMapLayout && (
    vm.lastScaleMapLayout.isDualMap !== currentMapLayout.isDualMap ||
    vm.lastScaleMapLayout.orientation !== currentMapLayout.orientation
  )

  if (!isPrintMode && currentMapLayout) {
    vm.lastScaleMapLayout = currentMapLayout
  }

  // 印刷モード用のスケールを処理
  document.querySelectorAll<HTMLElement>('.scale-for-print-mode').forEach(scale => {
    if (isPrintMode) {
      // 印刷モード：印刷用スケールを表示（左下固定）
      scale.style.display = 'block'
      // 位置を強制的に固定（他の操作の影響を受けないようにする）
      scale.style.position = 'absolute'
      scale.style.left = '10px'
      scale.style.bottom = '10px'
      scale.style.top = 'auto'
      scale.style.right = 'auto'
      scale.style.transform = 'none'
      scale.style.margin = '0'
    } else {
      // 通常モード：印刷用スケールを非表示
      scale.style.display = 'none'
    }
  })

  // 通常モード用のスケールを処理
  document.querySelectorAll<HTMLElement>('.scale-normal-mode').forEach(scale => {
    const mapName = scale.dataset.mapName || 'default'
    const container = scale.closest('.maplibregl-ctrl-bottom-left')
    if (isPrintMode) {
      // 印刷モード：通常用スケールを非表示
      scale.style.display = 'none'
    } else {
      // 通常モード時
      if (isSmartPhone) {
        // スマホ時は非表示
        scale.style.display = 'none'
        scale.classList.remove('scale-bottom-center')
        scale.classList.remove('scale-dragged')
      } else if (vm.is4ScreenMode) {
        // 4画面モード時は非表示
        scale.style.display = 'none'
        scale.classList.remove('scale-bottom-center')
      } else {
        // PC時：初期表示時のみ中央に表示（ドラッグ済みの場合は現在の位置を維持）
        scale.style.display = 'block'
        // ドラッグされていない場合のみ中央表示にする
        if (!scale.classList.contains('scale-dragged')) {
          scale.classList.add('scale-bottom-center')
        }
      }
    }
  })
}

/**
 * PC時のみスケールをドラッグ可能にする
 */
export function initScaleDraggable(vm: VM): void {
  if (vm.s_isSmartPhone) return

  // 通常モード用のスケールのみをドラッグ可能にする（印刷用スケールは除外）
  document.querySelectorAll<HTMLElement>('.scale-normal-mode').forEach(scale => {
    // 既にドラッグ可能になっている場合はスキップ
    if (scale.dataset.draggable === 'true') return

    scale.dataset.draggable = 'true'
    scale.style.cursor = 'move'

    const mapName = scale.dataset.mapName || 'default'
    const container = scale.closest<HTMLElement>('.maplibregl-ctrl-bottom-left')
    if (!container) return  // containerが見つからない場合はスキップ

    let isDragging = false
    let startX = 0
    let startY = 0
    let initialLeft = 0
    let initialTop = 0

    const onMouseDown = (e: MouseEvent) => {
      // 左クリックのみ
      if (e.button !== 0) return

      isDragging = true
      startX = e.clientX
      startY = e.clientY

      // 現在の位置を取得
      const rect = scale.getBoundingClientRect()
      const containerRect = container?.getBoundingClientRect()
      initialLeft = containerRect ? rect.left - containerRect.left : rect.left
      initialTop = containerRect ? rect.top - containerRect.top : rect.top

      e.preventDefault()
      e.stopPropagation()
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return

      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      const newLeft = initialLeft + deltaX
      const newTop = initialTop + deltaY
      const containerRect = container?.getBoundingClientRect()
      const scaleRect = scale.getBoundingClientRect()
      const clampedLeft = containerRect ? Math.min(Math.max(newLeft, 0), containerRect.width - scaleRect.width) : newLeft
      const clampedTop = containerRect ? Math.min(Math.max(newTop, 0), containerRect.height - scaleRect.height) : newTop

      // スケールを移動
      scale.classList.remove('scale-bottom-center')
      scale.classList.add('scale-dragged')
      scale.style.left = `${clampedLeft}px`
      scale.style.top = `${clampedTop}px`
      scale.style.bottom = 'auto'
      scale.style.transform = 'none'

      e.preventDefault()
    }

    const onMouseUp = (e: MouseEvent) => {
      if (!isDragging) return

      isDragging = false

      e.preventDefault()
    }

    scale.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    // クリーンアップ用（必要に応じて）
    // @ts-ignore
    scale._dragCleanup = () => {
      scale.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  })
}

/**
 * 4画面モード同期用（iframeからの利用時）
 */
export function setup4ScreenSync(vm: VM, map: maplibregl.Map): void {
  // iframe内でなければ無視（通常のOH3利用時は何もしない）
  if (window.parent === window) return

  // 親ウィンドウが4screen.htmlかどうかを確認するため、
  // 親からのメッセージを受信できるかで判定
  let is4ScreenMode = false
  let isSyncUpdating = false // 同期更新中フラグ（ループ防止）

  // マップ移動時に親ウィンドウへメッセージ送信
  const sendMapPosition = () => {
    if (!is4ScreenMode || isSyncUpdating) return
    const center = map.getCenter()
    const zoom = map.getZoom()
    const bearing = map.getBearing()
    const pitch = map.getPitch()
    window.parent.postMessage({
      type: 'mapSync',
      center: { lng: center.lng, lat: center.lat },
      zoom: zoom,
      bearing: bearing,
      pitch: pitch
    }, '*')
  }

  map.on('moveend', sendMapPosition)
  map.on('zoomend', sendMapPosition)
  map.on('rotateend', sendMapPosition)
  map.on('pitchend', sendMapPosition)

  // 親ウィンドウからのメッセージを受信してマップ位置を更新
  window.addEventListener('message', (event) => {
    const data = event.data

    // 4screenモード開始通知
    if (data.type === 'enable4ScreenSync') {
      is4ScreenMode = true
      // 4画面モードではiframe制限を解除してフルUIを表示
      vm.$store.state.isIframe = false
      vm.$store.state.is4ScreenMode = true // 4画面モードフラグを設定
      return
    }

    if (data.type !== 'mapSyncUpdate') return
    if (!is4ScreenMode) return

    isSyncUpdating = true
    map.jumpTo({
      center: [data.center.lng, data.center.lat],
      zoom: data.zoom,
      bearing: data.bearing,
      pitch: data.pitch
    })
    // フラグを解除（少し遅延させてループ防止）
    setTimeout(() => {
      isSyncUpdating = false
    }, 100)
  })

  // 親に準備完了を通知
  window.parent.postMessage({ type: 'mapSyncReady' }, '*')
}

/**
 * 画面モード切り替え（1/2/4画面）
 */
export function setScreenMode(vm: VM, mode: number): void {
  // 行政界履歴ドロワーを閉じてマーカーを削除
  if (vm.$store.state.gyoseiHistoryDrawer.marker) {
    vm.$store.state.gyoseiHistoryDrawer.marker.remove()
  }
  vm.$store.commit('setGyoseiHistoryDrawer', { open: false, marker: null, data: null })

  if (mode === 1) {
    // 1画面モード
    vm.$store.state.is4ScreenMode = false
    vm.s_map2Flg = false
    vm.mapSize.map01.width = '100%'
    vm.mapSize.map01.height = '100%'
    vm.mapSize.map01.top = 0
    vm.mapSize.map01.left = 0
    vm.$nextTick(() => {
      const map01 = vm.$store.state.map01
      if (map01) {
        try { map01.resize() } catch (_: unknown) { /* ignore */ }
      }
    })
  } else if (mode === 2) {
    // 2画面モード — map01の位置をs_map2Flg変更前に保存（連動モードのsyncで上書きされるため）
    const map01 = vm.$store.state.map01
    const savedState = map01 ? {
      center: map01.getCenter(),
      zoom: map01.getZoom(),
      bearing: map01.getBearing(),
      pitch: map01.getPitch()
    } : null
    vm.$store.state.is4ScreenMode = false
    vm.s_map2Flg = true
    if (window.innerWidth > 1000) {
      vm.mapSize.map01.width = '50%'
      vm.mapSize.map01.height = '100%'
      vm.mapSize.map02.width = '50%'
      vm.mapSize.map02.height = '100%'
      vm.mapSize.map02.top = 0
      vm.mapSize.map02.right = 0
    } else {
      vm.mapSize.map01.width = '100%'
      vm.mapSize.map01.height = '50%'
      vm.mapSize.map02.width = '100%'
      vm.mapSize.map02.height = '50%'
      vm.mapSize.map02.top = '50%'
      vm.mapSize.map02.right = 0
    }
    vm.ensureMap02Layers()
    vm.syncMap02ToMap01(savedState)
    // @ts-ignore - mapNameはオプショナル引数（popup.js側の型定義不足）
    if (typeof closeAllPopups === 'function') closeAllPopups()
  } else if (mode === 4) {
    // 4画面モード
    vm.$store.state.is4ScreenMode = true
    vm.s_map2Flg = false
    // 4画面レイアウト（2x2グリッド）
    vm.mapSize.map01 = { top: 0, left: 0, width: '50%', height: '50%' }
    vm.mapSize.map02 = { top: 0, left: '50%', width: '50%', height: '50%' }
    vm.mapSize.map03 = { top: '50%', left: 0, width: '50%', height: '50%' }
    vm.mapSize.map04 = { top: '50%', left: '50%', width: '50%', height: '50%' }
    // map02のレイヤーを確保
    vm.ensureMap02Layers()
    // DOMが更新された後にmap03, map04を初期化
    vm.$nextTick(() => {
      vm.initializeMap03And04(() => {
        vm.ensureMap03And04Layers()
        vm.$nextTick(() => {
          ;['map01', 'map02', 'map03', 'map04'].forEach((mapName: string) => {
            const map = vm.$store.state[mapName]
            if (map) {
              try { map.resize() } catch (_: unknown) { /* ignore */ }
            }
          })
          vm.setupNative4ScreenSync()
          // 4画面用ポインター同期をセットアップ
          vm.setup4ScreenPointerSync()
        })
      })
    })
  }
  vm.$nextTick(() => {
    if (!vm.isRightDiv2) {
      updateScaleDisplay(vm, false)
    }
  })
  // 画面モード変更後にパーマリンクを更新
  vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire
}

/**
 * 4画面プリセットを適用
 */
export function apply4ScreenPreset(vm: VM, presetName: string): void {
  // プリセット定義（各マップに設定するレイヤー）
  const presets: Record<string, { label: string; layers: Array<{ id: string; label: string; sourceId: string; tiles: string[] }> }> = {
    timeline: {
      // 時系列比較: 最新→87→79→74年の航空写真
      label: '時系列比較',
      layers: [
        { id: 'oh-seamlessphoto', label: '最新航空写真', sourceId: 'seamlessphoto', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg'] },
        { id: 'oh-sp87', label: '1987~90年航空写真', sourceId: 'sp87-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo4/{z}/{x}/{y}.jpg'] },
        { id: 'oh-sp79', label: '1979~83年航空写真', sourceId: 'sp79-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo2/{z}/{x}/{y}.jpg'] },
        { id: 'oh-sp74', label: '1974~78年航空写真', sourceId: 'sp74-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo1/{z}/{x}/{y}.jpg'] }
      ]
    },
    history: {
      // 歴史探索: 現在地図、迅速測図、旧版地形図、今昔マップ
      label: '歴史探索',
      layers: [
        { id: 'oh-stdLayer', label: '地理院標準地図', sourceId: 'stdSource', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'] },
        { id: 'oh-pale-layer', label: '地理院淡色地図', sourceId: 'pale-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'] },
        { id: 'oh-seamlessphoto', label: '最新航空写真', sourceId: 'seamlessphoto', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg'] },
        { id: 'oh-sp74', label: '1974~78年航空写真', sourceId: 'sp74-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo1/{z}/{x}/{y}.jpg'] }
      ]
    },
    realestate: {
      // 不動産調査: 地理院地図、航空写真、淡色地図、標準地図
      label: '不動産調査',
      layers: [
        { id: 'oh-pale-layer', label: '地理院淡色地図', sourceId: 'pale-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'] },
        { id: 'oh-seamlessphoto', label: '最新航空写真', sourceId: 'seamlessphoto', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg'] },
        { id: 'oh-sp74', label: '1974~78年航空写真', sourceId: 'sp74-source', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo1/{z}/{x}/{y}.jpg'] },
        { id: 'oh-stdLayer', label: '地理院標準地図', sourceId: 'stdSource', tiles: ['https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'] }
      ]
    }
  }
  const preset = presets[presetName]
  if (!preset) return

  // 4画面モードでない場合は切り替え
  if (!vm.is4ScreenMode) {
    setScreenMode(vm, 4)
  }

  // マップ初期化が完了してからレイヤーを設定
  const applyLayers = () => {
    const mapNames = ['map01', 'map02', 'map03', 'map04']
    const newSelectedLayers = { ...vm.$store.state.selectedLayers }

    preset.layers.forEach((layerDef, index) => {
      const mapName = mapNames[index]
      const map = vm.$store.state[mapName]
      if (!map) return

      // レイヤー設定オブジェクトを作成
      const layerConfig = {
        id: layerDef.id,
        label: layerDef.label,
        source: {
          id: layerDef.sourceId,
          obj: {
            type: 'raster',
            tiles: layerDef.tiles,
            tileSize: 256
          }
        },
        layers: [{
          id: layerDef.id,
          type: 'raster',
          source: layerDef.sourceId
        }],
        opacity: 1,
        visibility: true
      }

      // 既存レイヤーを全て削除してプリセットレイヤーのみにする
      newSelectedLayers[mapName] = [layerConfig]

      // マップに直接レイヤーを適用
      try {
        // 既存のoh-で始まるレイヤーを削除
        const existingLayers = map.getStyle()?.layers || []
        for (let i = existingLayers.length - 1; i >= 0; i--) {
          if (existingLayers[i].id.startsWith('oh-')) {
            map.removeLayer(existingLayers[i].id)
          }
        }
        // ソースを追加
        if (!map.getSource(layerDef.sourceId)) {
          map.addSource(layerDef.sourceId, {
            type: 'raster',
            tiles: layerDef.tiles,
            tileSize: 256
          })
        }
        // レイヤーを追加
        if (!map.getLayer(layerDef.id)) {
          map.addLayer({
            id: layerDef.id,
            type: 'raster',
            source: layerDef.sourceId
          })
        }
      } catch (e) {
        console.warn(`[4ScreenPreset] ${mapName} レイヤー適用エラー:`, e)
      }
    })

    // storeを更新
    vm.$store.state.selectedLayers = newSelectedLayers
  }

  // 4画面が初期化されるのを待つ
  if (vm.is4ScreenMode && vm.$store.state.map03 && vm.$store.state.map04) {
    applyLayers()
  } else {
    // 初期化完了を待つ
    const checkInterval = setInterval(() => {
      if (vm.$store.state.map03 && vm.$store.state.map04) {
        clearInterval(checkInterval)
        vm.$nextTick(applyLayers)
      }
    }, 100)
    // 5秒でタイムアウト
    setTimeout(() => clearInterval(checkInterval), 5000)
  }
}

/**
 * 4画面モードの切り替え
 */
export function toggle4ScreenMode(vm: VM): void {
  const current = vm.$store.state.is4ScreenMode
  if (current) {
    // 4画面 → 1画面へ
    vm.$store.state.is4ScreenMode = false
    vm.s_map2Flg = false
    vm.mapSize.map01.width = '100%'
    vm.mapSize.map01.height = '100%'
    vm.mapSize.map01.top = 0
    vm.mapSize.map01.left = 0
  } else {
    // 4画面へ
    vm.$store.state.is4ScreenMode = true
    vm.s_map2Flg = false // 2画面モードは解除
    // 4画面レイアウト（2x2グリッド）
    vm.mapSize.map01 = { top: 0, left: 0, width: '50%', height: '50%' }
    vm.mapSize.map02 = { top: 0, left: '50%', width: '50%', height: '50%' }
    vm.mapSize.map03 = { top: '50%', left: 0, width: '50%', height: '50%' }
    vm.mapSize.map04 = { top: '50%', left: '50%', width: '50%', height: '50%' }
    // map02のレイヤーを確保
    vm.ensureMap02Layers()
    // DOMが更新された後にmap03, map04を初期化
    vm.$nextTick(() => {
      // map03, map04を初期化（マップloadイベント後にレイヤー追加）
      vm.initializeMap03And04(() => {
        // 各マップのレイヤーを確保（マップload完了後に呼ばれる）
        vm.ensureMap03And04Layers()
        // リサイズ
        vm.$nextTick(() => {
          ;['map01', 'map02', 'map03', 'map04'].forEach((mapName: string) => {
            const map = vm.$store.state[mapName]
            if (map) {
              try { map.resize() } catch (_: unknown) { /* ignore */ }
            }
          })
          // 4画面同期を有効化
          vm.setupNative4ScreenSync()
        })
      })
    })
  }
}

/**
 * ポインターイベント同期のセットアップ
 */
export function setupPointerSync(vm: VM): void {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  if (isTouchDevice) return // タッチデバイスでは表示しない

  // 全ポインターを非表示にするヘルパー
  const hideAllPointers = () => {
    const pointerIds = ['pointer1', 'pointer2', 'pointer3', 'pointer4']
    pointerIds.forEach(id => {
      const el = document.getElementById(id)
      if (el) el.style.display = 'none'
    })
  }

  // 指定マップ以外のポインターを更新するヘルパー
  const updateOtherPointers = (sourceMapName: string, lngLat: { lng: number; lat: number }) => {
    const mapNames = vm.is4ScreenMode
      ? ['map01', 'map02', 'map03', 'map04']
      : ['map01', 'map02']

    mapNames.forEach((mapName: string, index: number) => {
      if (mapName === sourceMapName) return
      const map = vm.$store.state[mapName]
      const pointerId = `pointer${index + 1}`
      const pointerEl = document.getElementById(pointerId)
      if (!map || !pointerEl) return

      // 座標を変換
      const projectedPoint = map.project(lngLat)
      pointerEl.style.left = `${projectedPoint.x}px`
      pointerEl.style.top = `${projectedPoint.y}px`
      pointerEl.style.display = 'block'
    })
  }

  // 各マップにイベントを登録
  const setupMapPointerEvents = (mapName: string, pointerIndex: number) => {
    const map = vm.$store.state[mapName]
    if (!map) return

    // mousemove: 他マップのポインターを更新
    map.on('mousemove', (e: { lngLat: { lng: number; lat: number } }) => {
      if (vm.s_isPrint || vm.swipeMode) {
        hideAllPointers()
        return
      }
      // 4画面モードでない場合、map03/map04は無視
      if (!vm.is4ScreenMode && (mapName === 'map03' || mapName === 'map04')) {
        return
      }
      // 2画面モードでない場合、map02は無視
      if (!vm.s_map2Flg && !vm.is4ScreenMode && mapName === 'map02') {
        return
      }

      // 自分のポインターは非表示
      const ownPointer = document.getElementById(`pointer${pointerIndex}`)
      if (ownPointer) ownPointer.style.display = 'none'

      // 他マップのポインターを更新
      updateOtherPointers(mapName, e.lngLat)
    })

    // mouseleave: 全ポインターを非表示
    map.on('mouseleave', () => {
      hideAllPointers()
    })
  }

  // map01とmap02のイベントを登録
  setupMapPointerEvents('map01', 1)
  setupMapPointerEvents('map02', 2)

  // ブラウザウィンドウ外に出た時もポインターを非表示
  document.addEventListener('mouseleave', hideAllPointers)
  // visibilitychangeでタブ切り替え時も非表示
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) hideAllPointers()
  })
}

/**
 * スワイプ比較モードの切り替え
 */
export function toggleSwipeMode(vm: VM): void {
  if (vm.swipeMode) {
    // スワイプモード終了
    vm.swipeMode = false
    vm.mapSyncMaster = 'map01'
    vm.disableSwipeFollow()
    vm.disableSwipeNudge()
    vm.disableSwipeManualPan()
    vm.setMapInteractions(vm.$store.state.map01, true)
    vm.setMapInteractions(vm.$store.state.map02, true)
    vm.stopMapSync()
    vm.forceEnableMapInteractions(vm.$store.state.map01)
    vm.forceEnableMapInteractions(vm.$store.state.map02)
    // 1画面モードへ戻す
    vm.s_map2Flg = false
    vm.mapSize.map01.width = '100%'
    vm.mapSize.map01.height = '100%'
    vm.mapSize.map02.width = '50%'
    vm.mapSize.map02.height = '100%'
    vm.mapSize.map02.top = 0
    vm.mapSize.map02.right = 0
    // 念のため次フレームでも再度有効化
    vm.$nextTick(() => {
      vm.setMapInteractions(vm.$store.state.map01, true)
      vm.setMapInteractions(vm.$store.state.map02, true)
      vm.forceEnableMapInteractions(vm.$store.state.map01)
      vm.forceEnableMapInteractions(vm.$store.state.map02)
    })
  } else {
    // 小画面ではスワイプモード使用不可
    if (vm.isSmall500) {
      return
    }
    // スワイプモード開始
    if (!vm.s_map2Flg) {
      // 2画面モードでなければ先に有効化
      vm.s_map2Flg = true
    }
    // map02のレイヤーが空なら初期レイヤーを追加
    vm.ensureMap02Layers()
    // 両方のマップを全画面サイズに
    vm.mapSize.map01.width = '100%'
    vm.mapSize.map01.height = '100%'
    vm.mapSize.map02.width = '100%'
    vm.mapSize.map02.height = '100%'
    vm.mapSize.map02.top = 0
    vm.mapSize.map02.right = 0

    vm.mapSyncMaster = 'map02'
    vm.swipeMode = true
    vm.swipePosition = 50
    // スワイプ中は同期を完全停止（map02のみ操作）
    vm.stopMapSync()
    vm.disableSwipeFollow()
    vm.disableSwipeNudge()
    vm.setMapInteractions(vm.$store.state.map01, false)
    vm.setMapInteractions(vm.$store.state.map02, true)
    // 念のためズーム系インタラクションを有効化（他機能で無効化されていても復帰させる）
    const maps = [vm.$store.state.map01, vm.$store.state.map02].filter(Boolean)
    maps.forEach((m: maplibregl.Map) => {
      try {
        m.scrollZoom.enable()
        m.doubleClickZoom.enable()
        m.touchZoomRotate.enable()
        // @ts-ignore
        if (m.touchZoomRotate && m.touchZoomRotate.disableRotation) {
          // @ts-ignore
          m.touchZoomRotate.disableRotation()
        }
      } catch (_) { /* ignore */ }
    })

    vm.$nextTick(() => {
      const map02 = vm.$store.state.map02
      if (map02) {
        try { map02.resize() } catch (_: unknown) { /* ignore */ }
      }
    })
  }
}

/**
 * 印刷タイトルの高さを更新
 */
export function updatePrintTitleHeight(vm: VM): void {
  vm.$nextTick(() => {
    let printTitleEl = vm.$refs.printTitle

    // 配列の場合は最初の要素を取得
    if (Array.isArray(printTitleEl)) {
      printTitleEl = printTitleEl[0]
    }

    if (printTitleEl && printTitleEl.querySelector) {
      const titleBg = printTitleEl.querySelector('.print-title-bg')
      if (titleBg) {
        const height = titleBg.offsetHeight
        vm.printTitleHeight = height > 0 ? height + 8 : 8
      } else {
        // タイトルがない場合（dotのみ）
        vm.printTitleHeight = 8
      }
    }
  })
}
